import type { Signal} from '@angular/core';
import { Injectable, signal } from '@angular/core';

export type ToastSeverity = 'success' | 'info' | 'warning' | 'error';

export interface Toast {
  readonly id: string;
  readonly severity: ToastSeverity;
  readonly title: string;
  readonly detail?: string;
  /** ms hasta auto-dismiss. 0 = pegajoso (requiere cierre manual). */
  readonly durationMs: number;
  /** trace-id de la request que originó el toast (soporte/observabilidad). */
  readonly traceId?: string;
  readonly createdAt: number;
}

const DEFAULT_DURATION: Record<ToastSeverity, number> = {
  success: 4000,
  info: 5000,
  warning: 8000,
  error: 0, // los errores quedan pegajosos hasta que el operador los cierra
};

/**
 * Máximo de toasts a la vista. Los errores son pegajosos: sin tope, una ráfaga los
 * apila hasta cubrir la pantalla y bloquear controles reales.
 */
const MAX_VISIBLES = 4;

/**
 * Bus de notificaciones desacoplado del DS.
 *
 * Cualquier singleton de core (interceptores, ErrorHandler, realtime) empuja
 * toasts acá; el organism de UI que vive en el shell los renderiza vía la señal
 * `toasts`. Así `core/` NO depende de `shared/ui`.
 */
@Injectable({ providedIn: 'root' })
export class ToastBusService {
  private readonly _toasts = signal<readonly Toast[]>([]);
  readonly toasts: Signal<readonly Toast[]> = this._toasts.asReadonly();

  private readonly timers = new Map<string, ReturnType<typeof setTimeout>>();

  show(toast: Omit<Toast, 'id' | 'createdAt' | 'durationMs'> & { durationMs?: number }): string {
    // DEDUPLICACIÓN. Los errores son pegajosos, y una pantalla que dispara varios
    // requests puede fallarlos todos a la vez: sin esto, N fallas idénticas apilan
    // N toasts eternos. Si ya hay uno igual a la vista, se reusa.
    const igual = this._toasts().find(
      (t) =>
        t.severity === toast.severity && t.title === toast.title && t.detail === toast.detail,
    );
    if (igual) return igual.id;

    const id =
      typeof crypto !== 'undefined' && 'randomUUID' in crypto
        ? crypto.randomUUID()
        : `toast-${Date.now()}-${Math.random().toString(36).slice(2)}`;

    const durationMs = toast.durationMs ?? DEFAULT_DURATION[toast.severity];
    const full: Toast = { ...toast, id, durationMs, createdAt: Date.now() };

    // TOPE. Ningún aviso puede dejar la app inusable: los toasts se pintan sobre
    // el contenido, y apilados llegan a TAPAR controles. Verificado en /fleet: 9
    // toasts de error cubrían el botón "Reintentar" del propio banner de error,
    // dejando al usuario sin forma de recuperarse del fallo. Se descartan los más
    // viejos, que además ya fueron leídos.
    this._toasts.update((list) => {
      const conElNuevo = [...list, full];
      const sobran = conElNuevo.length - MAX_VISIBLES;
      if (sobran <= 0) return conElNuevo;
      for (const viejo of conElNuevo.slice(0, sobran)) this.clearTimer(viejo.id);
      return conElNuevo.slice(sobran);
    });

    if (durationMs > 0) {
      this.timers.set(
        id,
        setTimeout(() => this.dismiss(id), durationMs),
      );
    }
    return id;
  }

  private clearTimer(id: string): void {
    const timer = this.timers.get(id);
    if (timer !== undefined) {
      clearTimeout(timer);
      this.timers.delete(id);
    }
  }

  success(title: string, detail?: string): string {
    return this.show({ severity: 'success', title, ...(detail !== undefined && { detail }) });
  }

  info(title: string, detail?: string): string {
    return this.show({ severity: 'info', title, ...(detail !== undefined && { detail }) });
  }

  warning(title: string, detail?: string): string {
    return this.show({ severity: 'warning', title, ...(detail !== undefined && { detail }) });
  }

  error(title: string, detail?: string, traceId?: string): string {
    return this.show({
      severity: 'error',
      title,
      ...(detail !== undefined && { detail }),
      ...(traceId !== undefined && { traceId }),
    });
  }

  dismiss(id: string): void {
    const timer = this.timers.get(id);
    if (timer) {
      clearTimeout(timer);
      this.timers.delete(id);
    }
    this._toasts.update((list) => list.filter((t) => t.id !== id));
  }

  clear(): void {
    for (const timer of this.timers.values()) {
      clearTimeout(timer);
    }
    this.timers.clear();
    this._toasts.set([]);
  }
}
